import { buildDashboardPath } from './dashboardRoutes';
import {
    cleanImportSubject,
    senderLabelFromEmail,
    UNKNOWN_SENDER_LABEL,
    type EmailImportPaperRow,
    type EmailImportRow,
} from './paperInboxViewModel';
import { buildPaperInboxImportItems, type PaperInboxOutcome } from './paperInboxLog';
import type { LocalizedMessage } from './i18n/messages';
import { msg } from '@lingui/core/macro';

export const FORWARDED_ONBOARDING_SOURCE = 'forwarded';

export interface ForwardedPreviewItem {
    id: string;
    title: string;
    senderLabel: LocalizedMessage;
    statusLabel: LocalizedMessage;
}

export interface ForwardedOnboardingSnapshot {
    importCount: number;
    importedPaperCount: number;
    previewItems: ForwardedPreviewItem[];
}

const PREVIEW_OUTCOME_ORDER: PaperInboxOutcome[] = ['matched', 'no_match', 'unresolved', 'processing'];

function previewKeyForCandidate(candidate: EmailImportPaperRow, title: string): string {
    return (
        candidate.paper_id
        || candidate.paper_resolution_key
        || candidate.canonical_url
        || candidate.raw_url
        || title.trim().toLowerCase()
    );
}

export function buildForwardedOnboardingSnapshot({
    imports,
    candidates,
}: {
    imports: EmailImportRow[];
    candidates: EmailImportPaperRow[];
}): ForwardedOnboardingSnapshot {
    const importItems = buildPaperInboxImportItems(imports, candidates);
    const previewItems: ForwardedPreviewItem[] = [];
    const seenKeys = new Set<string>();

    for (const item of importItems) {
        for (const outcome of PREVIEW_OUTCOME_ORDER) {
            for (const row of item.rows) {
                if (row.outcome !== outcome) {
                    continue;
                }

                const title = row.title.trim();
                if (!title) {
                    continue;
                }

                const dedupeKey = previewKeyForCandidate(row.candidate, title);
                if (seenKeys.has(dedupeKey)) {
                    continue;
                }

                seenKeys.add(dedupeKey);
                previewItems.push({
                    id: `${item.importRow.id}-${row.emailImportPaperId}`,
                    title,
                    senderLabel: (() => {
                        const sender = senderLabelFromEmail(
                            item.importRow.original_sender_email,
                            item.importRow.subject,
                        );
                        return sender === UNKNOWN_SENDER_LABEL
                            ? msg({ id: 'inbox.unknownSender', message: 'Unknown sender' })
                            : sender;
                    })(),
                    statusLabel: previewStatusLabel(row.outcome),
                });

                if (previewItems.length >= 3) {
                    return {
                        importCount: imports.length,
                        importedPaperCount: imports.reduce(
                            (sum, importRow) => sum + Math.max(importRow.parsed_paper_count || 0, 0),
                            0
                        ),
                        previewItems,
                    };
                }
            }
        }
    }

    return {
        importCount: imports.length,
        importedPaperCount: imports.reduce(
            (sum, importRow) => sum + Math.max(importRow.parsed_paper_count || 0, 0),
            0
        ),
        previewItems,
    };
}

function previewStatusLabel(outcome: PaperInboxOutcome): LocalizedMessage {
    switch (outcome) {
        case 'matched':
            return msg({ id: 'forwardedPreview.status.matched', message: 'Matched' });
        case 'no_match':
            return msg({ id: 'forwardedPreview.status.noMatch', message: 'No match' });
        case 'unresolved':
            return msg({ id: 'forwardedPreview.status.skipped', message: 'Skipped' });
        case 'processing':
            return msg({ id: 'forwardedPreview.status.processing', message: 'Processing' });
    }
}

export function buildForwardedOnboardingRoute(pathname: string | null | undefined): string {
    return buildDashboardPath(pathname, '/onboarding/source-connected');
}

export function buildForwardedDeliveryRoute(
    pathname: string | null | undefined,
    projectId: string
): string {
    return buildDashboardPath(
        pathname,
        `/${projectId}/delivery?setup_source=${FORWARDED_ONBOARDING_SOURCE}`
    );
}

export function buildForwardedPreparingRoute(
    pathname: string | null | undefined,
    projectId: string
): string {
    return buildDashboardPath(
        pathname,
        `/${projectId}/settings?onboarding=preparing&setup_source=${FORWARDED_ONBOARDING_SOURCE}`
    );
}

export function isForwardedOnboardingSource(value: string | null | undefined): boolean {
    return String(value || '').trim().toLowerCase() === FORWARDED_ONBOARDING_SOURCE;
}

export function formatImportedPaperCount(importedPaperCount: number): LocalizedMessage {
    if (importedPaperCount <= 0) {
        return msg({ id: 'forwardedPreview.alertReceived', message: 'Forwarded alert received' });
    }

    return {
        ...msg({ id: 'forwardedPreview.paperCount', message: '{count, plural, one {# paper imported} other {# papers imported}}' }),
        values: { count: importedPaperCount },
    };
}

export function formatForwardedImportCount(importCount: number): LocalizedMessage {
    return {
        ...msg({ id: 'forwardedPreview.importCount', message: '{count, plural, one {# alert connected} other {# alerts connected}}' }),
        values: { count: importCount },
    };
}

export function fallbackForwardedImportMessage(imports: EmailImportRow[]): LocalizedMessage {
    if (imports.length === 0) {
        return msg({ id: 'forwardedPreview.received', message: 'We received your forwarded alert.' });
    }

    const subject = cleanImportSubject(imports[0]?.subject);
    if (subject && subject !== 'Untitled import') {
        return {
            ...msg({ id: 'forwardedPreview.receivedSubject', message: 'We received “{subject}”. Imported papers will appear in Inbox as they are resolved.' }),
            values: { subject },
        };
    }

    return msg({ id: 'forwardedPreview.receivedPending', message: 'We received your forwarded alert. Imported papers will appear in Inbox as they are resolved.' });
}
